// ETF Wealth Builder Calculator -- TanStack Query mutation for the PUBLIC
// Monte-Carlo simulation endpoint.
//
// We use a MUTATION (not a query) because the simulation is driven by explicit
// user intent (a POST payload) and has side-effect-like "run now" semantics.
// This also gives us the ergonomic `isPending` flag to show a loading state
// while the backend's anyio worker thread executes the Numba C-kernel.
//
// AUTH / BASE-URL NOTE: this is an UNAUTHENTICATED marketing endpoint, so we
// deliberately do NOT go through the shared `apiClient` (which attaches a
// Bearer token and, on a 401, silently refreshes / bounces the visitor to the
// WordPress login page -- both wrong for an anonymous frontpage tool). Instead
// we spin up a dedicated axios instance that REUSES apiClient's already-resolved
// baseURL (so it correctly targets forge-api.ki-wealth.com/api/v1 in production
// and the same-origin Vite proxy in dev) but carries NONE of its interceptors.

import { useMutation } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../../../../api/client';
import type { SimulationRequest, SimulationResponse } from '../types';

// Reuse the resolved base URL (`/api/v1` in dev, `https://host/api/v1` in prod)
// but with a clean interceptor-free client. Timeout is generous: the very first
// request after a cold backend boot pays a one-off ~0.5-1s Numba JIT compile.
const publicApiClient = axios.create({
    baseURL: apiClient.defaults.baseURL,
    timeout: 20_000,
    headers: { 'Content-Type': 'application/json' },
});

// Matches the confirmed backend route: the feature-slice router prefix
// (`/free-calculators/etf-wealth-builder-calculator`) is appended to the
// baseURL's `/api/v1` segment.
const SIMULATE_ENDPOINT =
    '/free-calculators/etf-wealth-builder-calculator/simulate-savings';

const simulateSavings = async (
    payload: SimulationRequest,
): Promise<SimulationResponse> => {
    try {
        const { data } = await publicApiClient.post<SimulationResponse>(
            SIMULATE_ENDPOINT,
            payload,
        );
        return data;
    } catch (error) {
        // Surface the backend's structured `{ detail }` (e.g. 404 unknown
        // ticker / 400 insufficient history) as a clean Error message the
        // form can render, falling back to a generic message otherwise.
        if (axios.isAxiosError(error)) {
            const detail = (error.response?.data as { detail?: string } | undefined)
                ?.detail;
            throw new Error(detail || 'Simulation engine failed. Please try again.');
        }
        throw error instanceof Error
            ? error
            : new Error('Simulation engine failed. Please try again.');
    }
};

export const useSimulateSavings = () => {
    return useMutation({
        mutationFn: simulateSavings,
    });
};
